import {
  Component,
  ChangeDetectionStrategy,
  inject,
  input,
  signal,
  effect,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PatientDocument } from '../../models/patient-document.type';
import { PatientDocumentService } from '../../services/database/patient-document.service';
import { MembershipService } from '../../services/auth/membership.service';
import { isStorageDenied } from '../../models/billing.type';

type PendingUpload = {
  key: string;
  fileName: string;
};

@Component({
  selector: 'app-supporting-documents',
  templateUrl: './supporting-documents.component.html',
  styleUrls: ['./supporting-documents.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
})
export class SupportingDocumentsComponent {
  private documents = inject(PatientDocumentService);
  private membership = inject(MembershipService);
  private snack = inject(MatSnackBar);

  /** Firestore patient id; uploads disabled until set. */
  readonly patientId = input<string | null>(null);
  /** True once the patient record exists in Firestore. */
  readonly uploadsEnabled = input(false);

  readonly items = signal<PatientDocument[]>([]);
  readonly loading = signal(false);
  readonly pendingUploads = signal<PendingUpload[]>([]);

  constructor() {
    effect(() => {
      const patientId = this.patientId();
      const enabled = this.uploadsEnabled();
      if (!patientId || !enabled) {
        this.items.set([]);
        return;
      }
      void this.reload(patientId);
    });
  }

  async onFilesSelected(event: Event): Promise<void> {
    const patientId = this.patientId();
    if (!patientId || !this.uploadsEnabled()) {
      return;
    }

    const inputEl = event.target as HTMLInputElement;
    const files = inputEl.files;
    if (!files?.length) {
      return;
    }

    const storageError = this.membership.storageUploadError(Array.from(files));
    if (storageError) {
      this.snack.open(storageError, '', { duration: 6000 });
      inputEl.value = '';
      return;
    }

    for (const file of Array.from(files)) {
      const key = `${file.name}-${Date.now()}-${Math.random()}`;
      this.pendingUploads.update((pending) => [...pending, { key, fileName: file.name }]);

      try {
        const created = await this.uploadWithRetry(patientId, file);
        this.membership.bumpStorageBytes(file.size);
        this.items.update((list) =>
          [created, ...list].sort(
            (a, b) => b.uploadedAt.toMillis() - a.uploadedAt.toMillis()
          )
        );
        this.snack.open(`${file.name} se guardó correctamente.`, '', { duration: 3000 });
      } catch (err) {
        this.snack.open(
          isStorageDenied(err)
            ? `${file.name} no pudo guardarse: el almacenamiento del consultorio está lleno.`
            : `${file.name} no pudo guardarse.`,
          '',
          { duration: 5000 }
        );
      } finally {
        this.pendingUploads.update((pending) => pending.filter((p) => p.key !== key));
      }
    }

    inputEl.value = '';
  }

  async download(document: PatientDocument): Promise<void> {
    try {
      const url = await this.documents.downloadDocument(document);
      window.open(url, '_blank');
    } catch {
      this.snack.open('No se pudo descargar el archivo.', '', { duration: 4000 });
    }
  }

  async remove(document: PatientDocument): Promise<void> {
    const patientId = this.patientId();
    if (!patientId) {
      return;
    }
    try {
      await this.documents.deleteDocument(patientId, document);
      this.membership.bumpStorageBytes(-(document.sizeBytes ?? 0));
      this.items.update((list) => list.filter((item) => item.id !== document.id));
      this.snack.open('Documento eliminado.', '', { duration: 2500 });
    } catch {
      this.snack.open('No se pudo eliminar el documento.', '', { duration: 4000 });
    }
  }

  private async reload(patientId: string): Promise<void> {
    this.loading.set(true);
    try {
      this.items.set(await this.documents.listDocuments(patientId));
    } catch {
      this.items.set([]);
      this.snack.open('No se pudieron cargar los documentos.', '', { duration: 4000 });
    } finally {
      this.loading.set(false);
    }
  }

  private async uploadWithRetry(patientId: string, file: File, attempts = 3): Promise<PatientDocument> {
    let lastError: unknown;
    for (let i = 0; i < attempts; i++) {
      try {
        return await this.documents.uploadDocument(patientId, file);
      } catch (err) {
        lastError = err;
        if (isStorageDenied(err)) {
          break;
        }
        if (i < attempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
    }
    throw lastError;
  }
}
